import { useState, useEffect } from "react";
import { fetchAPI } from "@/lib/api";
/* Navbar removed */
import { Footer } from "@/components/layout/Footer";
import { X, Play } from "lucide-react";
import { SEO } from "@/components/SEO";

type MediaItem = {
  id: number;
  src: string;
  title: string;
  category: string;
  type: "image" | "video";
  video_url?: string;
  orientation?: "landscape" | "portrait";
};

const categories = ["All", "Sacred Places", "Meditation", "Rituals", "Wellness", "Yoga", "People", "Landscapes"];

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(null);
  const [galleryItems, setGalleryItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAPI("?type=gallery")
      .then((data: any) => {
        setGalleryItems(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching gallery:", err);
        setLoading(false);
      });
  }, []);

  const filteredItems = selectedCategory === "All" 
    ? galleryItems 
    : galleryItems.filter(item => item.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-saffron"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Visual Stories - Sacred Paths Gallery" 
        description="Explore visual stories from our spiritual journeys. A collection of photographs and videos capturing the essence of India's sacred places."
        canonical="https://baelleaves.com/gallery"
      />
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-sand to-background">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-saffron text-sm tracking-[0.2em] uppercase font-medium">
              Visual Stories
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground mt-4 mb-6">
              Moments of <span className="text-maroon">Sacred Beauty</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Each photograph and video captures a moment of transformation, devotion, or
              transcendence from our journeys through India's spiritual heartland.
            </p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-4 md:py-8 border-b border-border sticky top-16 md:top-20 bg-background/95 backdrop-blur-md z-40 overflow-x-auto no-scrollbar">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex gap-2 md:gap-3 min-w-max px-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  selectedCategory === category
                    ? "bg-saffron text-primary-foreground shadow-md"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-8 md:py-16">
        <div className="container mx-auto px-2 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4 auto-rows-[150px] md:auto-rows-[250px]">
            {filteredItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className={`group relative overflow-hidden rounded-lg md:rounded-xl bg-muted ${
                  item.orientation === 'portrait' 
                    ? "row-span-2 aspect-[3/4]" 
                    : "aspect-video"
                }`}
              >
                <img
                  src={item.src || (item.video_url?.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/) 
                    ? `https://img.youtube.com/vi/${item.video_url?.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1]}/hqdefault.jpg` 
                    : item.video_url)}
                  alt={item.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                {item.type === "video" && (
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="w-10 h-10 md:w-14 md:h-14 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform border border-white/20">
                      <Play className="w-5 h-5 md:w-7 md:h-7 text-white fill-white ml-1" />
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="font-serif text-white text-sm md:text-lg line-clamp-1">{item.title}</p>
                  <p className="text-white/70 text-xs md:text-sm">{item.category}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedItem && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center animate-in fade-in duration-200"
          onClick={() => setSelectedItem(null)}
        >
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 md:top-8 md:right-8 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors z-[60]"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedItem(null);
            }}
          >
            <X className="w-6 h-6 md:w-8 md:h-8" />
          </button>

          <div 
            className="w-full h-full flex flex-col items-center justify-center p-4 md:p-8" 
            onClick={(e) => e.stopPropagation()}
          >
             {selectedItem.type === "video" ? (
               selectedItem.video_url?.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1] ? (
                 <div className="w-full max-w-5xl aspect-video relative bg-black rounded-lg overflow-hidden shadow-2xl">
                    <iframe 
                        className="absolute top-0 left-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${selectedItem.video_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1]}?autoplay=1&modestbranding=1&rel=0`}
                        title={selectedItem.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                        allowFullScreen
                        loading="lazy"
                    ></iframe>
                 </div>
               ) : (
                <video
                    autoPlay
                    controls
                    className="max-w-full max-h-[80vh] w-auto h-auto object-contain rounded-lg shadow-2xl bg-black"
                    preload="none"
                >
                    <source src={selectedItem.video_url} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
               )
            ) : (
              <img
                src={selectedItem.src}
                alt={selectedItem.title}
                className="max-w-full max-h-[80vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
                loading="lazy"
              />
            )}
            
            {/* Details Overlay */}
            <div className="mt-6 md:mt-8 text-center max-w-2xl px-4 animate-in slide-in-from-bottom-4 duration-300">
              <h3 className="font-serif text-2xl md:text-3xl text-white mb-2">{selectedItem.title}</h3>
              <p className="text-white/70 text-sm md:text-base tracking-wide uppercase">{selectedItem.category}</p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Gallery;